<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload.vue';
import CustomerFraudUploading from '@/V2/customers/fraud/CustomerFraudUploading.vue';
import CustomerFraudDocumentsFullList from '@/V2/customers/fraud/CustomerFraudDocumentsFullList.vue';
import {
  AgentRunStatus,
  CustomerFraudReasoningStatusForUi,
  parseCustomerFraudReasoningResult,
} from '@/utils/customerFraudReasoning';
import CustomerFraudReasoningInsights from '@/V2/customers/overview/CustomerFraudReasoningInsights.vue';
import CustomerFraudReasoningAnalysis from '@/V2/customers/overview/CustomerFraudReasoningAnalysis.vue';
import { hasAgentRunInProgress } from '@/utils/ai-agents';

const store = useStore();
const route = useRoute();

const uploadDocumentsRef = useTemplateRef('documentsTitleUpload');

const customerId = computed<string>(() => route.params.customer_id as string);

const customer = computed<Record<string, unknown> | undefined>(() =>
  store.getters.getCachedCustomerById(customerId.value),
);

const customerFraudReasoningResult = computed(() =>
  parseCustomerFraudReasoningResult(customer.value),
);

const someDocumentUploadingOrProcessing = computed<boolean>(
  () => store.getters.someDocumentUploadingOrProcessing,
);

const isAnalyzingCustomer = computed<boolean>(() => {
  if (customer.value && !customer.value.risk_level) {
    return true;
  }
  const runs = store.getters.getCachedCustomerReviewAgentRunsForCustomerUUID(
    customerId.value,
  );
  return hasAgentRunInProgress(runs);
});

const fraudReasoningStatus = computed<CustomerFraudReasoningStatusForUi>(() => {
  if (someDocumentUploadingOrProcessing.value) {
    return CustomerFraudReasoningStatusForUi.PROCESSING_DOCUMENTS;
  }
  if (isAnalyzingCustomer.value) {
    return CustomerFraudReasoningStatusForUi.ANALYZING_CUSTOMER;
  }
  if (!customerFraudReasoningResult.value) {
    return CustomerFraudReasoningStatusForUi.NO_DATA;
  }
  if (
    customerFraudReasoningResult.value?.status === AgentRunStatus.PENDING ||
    customerFraudReasoningResult.value?.status === AgentRunStatus.RUNNING
  ) {
    return CustomerFraudReasoningStatusForUi.REASONING;
  }
  if (customerFraudReasoningResult.value?.status === AgentRunStatus.FINISHED) {
    return CustomerFraudReasoningStatusForUi.FINISHED;
  }
  if (customerFraudReasoningResult.value?.status === AgentRunStatus.FAILED) {
    return CustomerFraudReasoningStatusForUi.FAILED;
  }
  return CustomerFraudReasoningStatusForUi.NO_DATA;
});

const onUploadDocumentsClicked = () => {
  if (!uploadDocumentsRef.value) {
    return;
  }
  uploadDocumentsRef.value.startUpload();
};
</script>

<template>
  <div
    class="risk-analysis i-px-11 i-pt-11 i-pb-11 i-flex"
    data-testid="risk-analysis-page"
  >
    <div
      v-if="customer"
      class="risk-analysis-overview-container d-flex flex-column"
    >
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Fraud Reasoning"
        subtitle="View the fraud reasoning results for this customer"
        :expanded="false"
      >
      </documents-title-upload>

      <!-- content -->
      <div class="overview-content d-flex flex-column i-gap-8">
        <customer-fraud-uploading v-if="someDocumentUploadingOrProcessing" />

        <customer-fraud-reasoning-analysis
          :status="fraudReasoningStatus"
          :result="customerFraudReasoningResult"
        />

        <customer-fraud-reasoning-insights
          :status="fraudReasoningStatus"
          :result="customerFraudReasoningResult"
        />

        <customer-fraud-documents-full-list
          @upload-documents="onUploadDocumentsClicked"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.risk-analysis {
  overflow-y: auto;

  .risk-analysis-overview-container {
    max-width: 1200px;
    margin: 0 auto;
  }
}
</style>
